import React, { useEffect, useMemo, useState } from 'react';
import {
  Search,
  RefreshCw,
  AlertTriangle,
  FileText,
  Wallet,
  Calendar,
  Download,
  Filter,
  X,
  Plus,
  Trash2,
  Receipt,
  ChevronRight,
} from 'lucide-react';
import { fetchDigitalStrukReport } from '../../services/digitalJobService';
import {
  getPelunasan,
  getPelunasanTotals,
  addPelunasan,
  deletePelunasan,
  PELUNASAN_EVENT,
} from '../../services/pelunasanStore';
import { useIsCompactUser } from '../../helpers/currentUser';

const COLORS = {
  primary: '#a94007',
  accent: '#f97316',
  ink: '#1e293b',
  sub: '#64748b',
  border: '#e2e8f0',
  bgSoft: '#f8fafc',
};

const rupiah = (v) => 'Rp ' + Math.round(Number(v) || 0).toLocaleString('id-ID');
const fmtNum = (v) => (Number(v) || 0).toLocaleString('id-ID');

// status pembayaran per struk
const statusOf = (hutang, dibayar) => {
  if (hutang <= 0) return { key: 'LUNAS', label: 'Lunas', color: '#16a34a', bg: '#dcfce7' };
  const sisa = hutang - dibayar;
  if (sisa <= 0) return { key: 'LUNAS', label: 'Lunas', color: '#16a34a', bg: '#dcfce7' };
  if (dibayar > 0) return { key: 'SEBAGIAN', label: 'Sebagian', color: '#d97706', bg: '#fef3c7' };
  return { key: 'BELUM', label: 'Belum Bayar', color: '#dc2626', bg: '#fee2e2' };
};

const KpiCard = ({ icon: Icon, label, value, hint, tone = 'default' }) => {
  const tones = {
    default: { bg: '#fff', color: COLORS.ink, iconBg: '#fff7ed', iconColor: COLORS.accent },
    danger: { bg: '#fef2f2', color: '#991b1b', iconBg: '#fee2e2', iconColor: '#dc2626' },
    good: { bg: '#f0fdf4', color: '#166534', iconBg: '#dcfce7', iconColor: '#16a34a' },
  };
  const t = tones[tone] || tones.default;
  return (
    <div style={{ background: t.bg, border: `1px solid ${COLORS.border}`, borderRadius: '14px', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', flex: '1 1 200px', minWidth: 0 }}>
      <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: t.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} color={t.iconColor} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: COLORS.sub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
        <div style={{ fontSize: '20px', fontWeight: 800, color: t.color, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
        {hint && <div style={{ fontSize: '11px', color: COLORS.sub, marginTop: '2px' }}>{hint}</div>}
      </div>
    </div>
  );
};

// ---- Modal pelunasan per struk ----
const PelunasanModal = ({ struk, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('CASH');
  const [note, setNote] = useState('');

  const refresh = () => setEntries(getPelunasan(struk.strukno));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [struk.strukno]);

  const dibayar = entries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const sisa = Math.max(0, struk.hutang - dibayar);

  const submit = () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return;
    addPelunasan(struk.strukno, { amount: amt, date, method, note });
    setAmount('');
    setNote('');
    refresh();
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* header */}
        <div style={{ padding: '18px 20px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Receipt size={18} color={COLORS.primary} />
            <div>
              <div style={{ fontSize: '15px', fontWeight: 800, color: COLORS.ink, fontFamily: 'monospace' }}>{struk.strukno}</div>
              <div style={{ fontSize: '12px', color: COLORS.sub }}>{struk.customer} · {struk.strukdate}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: COLORS.sub }}><X size={20} /></button>
        </div>

        {/* ringkasan hutang */}
        <div style={{ display: 'flex', gap: '10px', padding: '16px 20px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 120px', background: COLORS.bgSoft, borderRadius: '10px', padding: '10px 12px' }}>
            <div style={{ fontSize: '11px', color: COLORS.sub }}>Hutang</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: COLORS.ink }}>{rupiah(struk.hutang)}</div>
          </div>
          <div style={{ flex: '1 1 120px', background: '#f0fdf4', borderRadius: '10px', padding: '10px 12px' }}>
            <div style={{ fontSize: '11px', color: '#166534' }}>Sudah Dibayar</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#166534' }}>{rupiah(dibayar)}</div>
          </div>
          <div style={{ flex: '1 1 120px', background: sisa > 0 ? '#fef2f2' : '#f0fdf4', borderRadius: '10px', padding: '10px 12px' }}>
            <div style={{ fontSize: '11px', color: sisa > 0 ? '#991b1b' : '#166534' }}>Sisa</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: sisa > 0 ? '#dc2626' : '#166534' }}>{rupiah(sisa)}</div>
          </div>
        </div>

        {/* form input pelunasan (partial) */}
        {struk.hutang > 0 && (
          <div style={{ padding: '0 20px 16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.ink, marginBottom: '8px' }}>Tambah Pelunasan {sisa > 0 && <span style={{ color: COLORS.sub, fontWeight: 400 }}>(boleh sebagian / partial)</span>}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 140px' }}>
                <label style={{ fontSize: '11px', color: COLORS.sub }}>Jumlah</label>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={sisa ? String(sisa) : '0'}
                  style={{ width: '100%', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '8px 10px', fontSize: '13px', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 130px' }}>
                <label style={{ fontSize: '11px', color: COLORS.sub }}>Tanggal</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  style={{ width: '100%', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '8px 10px', fontSize: '13px', outline: 'none' }} />
              </div>
              <div style={{ flex: '1 1 110px' }}>
                <label style={{ fontSize: '11px', color: COLORS.sub }}>Metode</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)}
                  style={{ width: '100%', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '8px 10px', fontSize: '13px', outline: 'none', background: '#fff' }}>
                  <option>CASH</option>
                  <option>TRANSFER</option>
                  <option>QRIS</option>
                  <option>LAINNYA</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional)"
                style={{ flex: 1, border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '8px 10px', fontSize: '13px', outline: 'none' }} />
              <button onClick={submit} disabled={!amount || Number(amount) <= 0}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: COLORS.primary, color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 600, cursor: (!amount || Number(amount) <= 0) ? 'not-allowed' : 'pointer', opacity: (!amount || Number(amount) <= 0) ? 0.5 : 1 }}>
                <Plus size={15} /> Simpan
              </button>
            </div>
          </div>
        )}

        {/* riwayat pelunasan */}
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.ink, marginBottom: '8px' }}>Riwayat Pelunasan ({entries.length})</div>
          {entries.length === 0 ? (
            <div style={{ fontSize: '13px', color: COLORS.sub, padding: '14px', textAlign: 'center', background: COLORS.bgSoft, borderRadius: '10px' }}>Belum ada pelunasan.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {entries.map((e) => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '8px 12px' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#166534' }}>{rupiah(e.amount)}</div>
                    <div style={{ fontSize: '11px', color: COLORS.sub }}>{e.date} · {e.method}{e.note ? ` · ${e.note}` : ''}</div>
                  </div>
                  <button onClick={() => { deletePelunasan(struk.strukno, e.id); refresh(); }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626' }}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: '10px 20px', borderTop: `1px solid ${COLORS.border}`, fontSize: '11px', color: COLORS.sub }}>
          Catatan: data pelunasan disimpan lokal di browser ini (belum ada tabel pelunasan di backend).
        </div>
      </div>
    </div>
  );
};

const DigitalStrukReport = () => {
  const compact = useIsCompactUser();

  const [startDate, setStartDate] = useState('2026-01-01');
  const [endDate, setEndDate] = useState('2026-12-31');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [expanded, setExpanded] = useState(null); // strukno yang baris item-nya dibuka
  const [modalStruk, setModalStruk] = useState(null);
  const [pelunasanVersion, setPelunasanVersion] = useState(0);

  const load = async () => {
    if (!startDate || !endDate) return;
    if (startDate > endDate) { setError('Tanggal mulai tidak boleh melebihi tanggal akhir.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetchDigitalStrukReport(startDate, endDate);
      setReport(res);
    } catch (e) {
      console.error(e);
      setError('Gagal memuat data. Periksa koneksi ke server sursys atau rentang tanggal.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // re-render saat pelunasan berubah (modal simpan/hapus)
  useEffect(() => {
    const onUpd = () => setPelunasanVersion((v) => v + 1);
    window.addEventListener(PELUNASAN_EVENT, onUpd);
    return () => window.removeEventListener(PELUNASAN_EVENT, onUpd);
  }, []);

  // join struk + pelunasan
  const joined = useMemo(() => {
    if (!report) return [];
    const totals = getPelunasanTotals();
    return report.struks.map((s) => {
      const dibayar = totals[s.strukno] || 0;
      const sisa = Math.max(0, s.hutang - dibayar);
      return { ...s, dibayar, sisa, status: statusOf(s.hutang, dibayar) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, pelunasanVersion]);

  const totalsAfter = useMemo(() => {
    const totalDibayar = joined.reduce((s, x) => s + x.dibayar, 0);
    const sisaHutang = joined.reduce((s, x) => s + x.sisa, 0);
    return { totalDibayar, sisaHutang };
  }, [joined]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return joined.filter((s) => {
      if (statusFilter !== 'ALL' && s.status.key !== statusFilter) return false;
      if (!q) return true;
      return (
        s.strukno.toLowerCase().includes(q) ||
        (s.customer || '').toLowerCase().includes(q) ||
        (s.wilayah || '').toLowerCase().includes(q) ||
        s.jobNos.some((j) => j.toLowerCase().includes(q))
      );
    });
  }, [joined, search, statusFilter]);

  const exportCsv = () => {
    if (!filtered.length) return;
    const header = ['No.Struk', 'Tanggal', 'Pemesan', 'Wilayah', 'Jml Item', 'No.Job', 'Total', 'Hutang', 'Dibayar', 'Sisa', 'Status'];
    const lines = [header.join(';')];
    filtered.forEach((s) => {
      lines.push([
        `"${s.strukno}"`, s.strukdate, `"${(s.customer || '').replace(/"/g, "'")}"`, `"${s.wilayah}"`,
        s.itemCount, `"${s.jobNos.join(' ')}"`, Math.round(s.total), Math.round(s.hutang),
        Math.round(s.dibayar), Math.round(s.sisa), s.status.label,
      ].join(';'));
    });
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `struk-digital_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pad = compact ? '12px 16px' : '24px 32px';

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bgSoft, padding: pad }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 800, color: COLORS.ink, margin: 0, letterSpacing: '-0.025em' }}>Rekap Per Nomor Struk</h1>
        <p style={{ color: COLORS.sub, fontSize: '14px', marginTop: '4px', marginBottom: 0 }}>
          Satu baris per nomor struk (bukan per item). Catat pelunasan hutang per struk, mendukung pembayaran sebagian.
        </p>
      </div>

      {/* Filter bar */}
      <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '14px', padding: '14px 16px', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '14px', marginBottom: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: COLORS.sub }}>Tanggal Mulai</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '6px 10px' }}>
            <Calendar size={15} color={COLORS.sub} />
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', color: COLORS.ink, background: 'transparent' }} />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: COLORS.sub }}>Tanggal Akhir</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '6px 10px' }}>
            <Calendar size={15} color={COLORS.sub} />
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', color: COLORS.ink, background: 'transparent' }} />
          </div>
        </div>
        <button onClick={load} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: COLORS.primary, color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 16px', fontSize: '13px', fontWeight: 600, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          <RefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'Memuat...' : 'Terapkan'}
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={exportCsv} disabled={!report} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', color: COLORS.ink, border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '9px 14px', fontSize: '13px', fontWeight: 600, cursor: report ? 'pointer' : 'not-allowed', opacity: report ? 1 : 0.5 }}>
          <Download size={15} /> Export CSV
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: '12px', padding: '12px 16px', marginBottom: '18px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {report && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '20px' }}>
          <KpiCard icon={FileText} label="Total Struk" value={fmtNum(report.summary.totalStruk)} hint={`${fmtNum(report.lineCount)} baris item`} />
          <KpiCard icon={Wallet} label="Total Nilai" value={rupiah(report.summary.totalNilai)} />
          <KpiCard icon={Wallet} label="Total Hutang" value={rupiah(report.summary.totalHutang)} hint={`${fmtNum(report.summary.strukBerhutang)} struk berhutang`} tone={report.summary.totalHutang > 0 ? 'danger' : 'default'} />
          <KpiCard icon={Wallet} label="Sisa Hutang" value={rupiah(totalsAfter.sisaHutang)} hint={`dibayar ${rupiah(totalsAfter.totalDibayar)}`} tone={totalsAfter.sisaHutang > 0 ? 'danger' : 'good'} />
        </div>
      )}

      {loading && !report && (
        <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '14px', padding: '40px', textAlign: 'center', color: COLORS.sub }}>
          <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', color: COLORS.accent }} />
          <div style={{ marginTop: '12px', fontSize: '14px' }}>Memuat data struk...</div>
        </div>
      )}

      {report && (
        <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '14px', overflow: 'hidden' }}>
          {/* controls */}
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '7px 12px', flex: '1 1 240px' }}>
              <Search size={15} color={COLORS.sub} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari no struk / pemesan / wilayah / no job..." style={{ border: 'none', outline: 'none', fontSize: '13px', width: '100%', color: COLORS.ink }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '7px 12px' }}>
              <Filter size={15} color={COLORS.sub} />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', color: COLORS.ink, background: 'transparent', cursor: 'pointer' }}>
                <option value="ALL">Semua Status</option>
                <option value="BELUM">Belum Bayar</option>
                <option value="SEBAGIAN">Sebagian</option>
                <option value="LUNAS">Lunas</option>
              </select>
            </div>
            <div style={{ fontSize: '12px', color: COLORS.sub, marginLeft: 'auto' }}>Menampilkan <b>{fmtNum(filtered.length)}</b> struk</div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '980px' }}>
              <thead>
                <tr style={{ background: COLORS.bgSoft, textAlign: 'left', color: COLORS.sub }}>
                  {['', 'No. Struk', 'No. Job', 'Tanggal', 'Pemesan', 'Wilayah', 'Item', 'Total', 'Hutang', 'Dibayar', 'Sisa', 'Status', ''].map((h, i) => (
                    <th key={i} style={{ padding: '11px 12px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', textAlign: i >= 7 && i <= 10 ? 'right' : 'left', borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={13} style={{ padding: '36px', textAlign: 'center', color: COLORS.sub }}>Tidak ada struk yang cocok.</td></tr>
                ) : (
                  filtered.map((s) => (
                    <React.Fragment key={s.strukno}>
                      <tr style={{ borderBottom: `1px solid ${COLORS.bgSoft}` }}>
                        <td style={{ padding: '8px 12px' }}>
                          <button onClick={() => setExpanded(expanded === s.strukno ? null : s.strukno)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: COLORS.sub, display: 'flex' }}>
                            <ChevronRight size={16} style={{ transform: expanded === s.strukno ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                          </button>
                        </td>
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 700, color: COLORS.primary, whiteSpace: 'nowrap' }}>{s.strukno}</td>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                          {s.jobNos && s.jobNos.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '160px' }}>
                              {s.jobNos.map((jn) => (
                                <span key={jn} style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, color: '#0369a1', background: '#e0f2fe', padding: '2px 6px', borderRadius: '5px' }}>{jn}</span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#dc2626', fontSize: '11px', fontWeight: 700 }}>TANPA JOB</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', color: COLORS.sub, whiteSpace: 'nowrap' }}>{s.strukdate}</td>
                        <td style={{ padding: '10px 12px', color: COLORS.ink }}>{s.customer}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span 
                              style={{ 
                                background: s.wilayahSource === 'datajob' ? '#dcfce7' : '#eef2ff', 
                                color: s.wilayahSource === 'datajob' ? '#166534' : '#3730a3', 
                                fontSize: '11px', 
                                fontWeight: 600, 
                                padding: '3px 8px', 
                                borderRadius: '6px', 
                                whiteSpace: 'nowrap' 
                              }}
                              title={s.wilayahSource === 'datajob' 
                                ? `✅ Wilayah ASLI dari datajob\nStruk: ${s.strukno}\nJob: ${s.jobNos.join(', ')}`
                                : `⚠️ Wilayah TURUNAN dari customer\nCustomer: ${s.customer}`
                              }
                            >
                              {s.wilayah}
                            </span>
                            {s.wilayahSource === 'datajob' && (
                              <span 
                                style={{ 
                                  fontSize: '10px', 
                                  fontWeight: 700,
                                  color: '#16a34a',
                                  background: '#f0fdf4',
                                  padding: '2px 5px',
                                  borderRadius: '4px',
                                  border: '1px solid #bbf7d0',
                                }}
                                title="Data dari sistem produksi (datajob)"
                              >
                                DB
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '10px 12px', color: COLORS.sub, whiteSpace: 'nowrap' }}>{s.itemCount} item</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: COLORS.ink, whiteSpace: 'nowrap' }}>{rupiah(s.total)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: s.hutang > 0 ? '#dc2626' : COLORS.sub, fontWeight: s.hutang > 0 ? 700 : 400, whiteSpace: 'nowrap' }}>{rupiah(s.hutang)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#166534', whiteSpace: 'nowrap' }}>{s.dibayar > 0 ? rupiah(s.dibayar) : '-'}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: s.sisa > 0 ? '#dc2626' : COLORS.sub, whiteSpace: 'nowrap' }}>{rupiah(s.sisa)}</td>
                        <td style={{ padding: '10px 12px' }}><span style={{ background: s.status.bg, color: s.status.color, fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '20px', whiteSpace: 'nowrap' }}>{s.status.label}</span></td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                          <button onClick={() => setModalStruk(s)} disabled={s.hutang <= 0 && s.dibayar <= 0}
                            style={{ display: 'flex', alignItems: 'center', gap: '5px', background: s.hutang > 0 ? '#fff7ed' : '#fff', color: s.hutang > 0 ? COLORS.primary : COLORS.sub, border: `1px solid ${s.hutang > 0 ? '#fed7aa' : COLORS.border}`, borderRadius: '7px', padding: '6px 10px', fontSize: '12px', fontWeight: 600, cursor: (s.hutang <= 0 && s.dibayar <= 0) ? 'not-allowed' : 'pointer', opacity: (s.hutang <= 0 && s.dibayar <= 0) ? 0.5 : 1 }}>
                            <Receipt size={13} /> Pelunasan
                          </button>
                        </td>
                      </tr>
                      {expanded === s.strukno && (
                        <tr>
                          <td colSpan={13} style={{ padding: '0 12px 12px 44px', background: COLORS.bgSoft }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', background: '#fff', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
                              <thead>
                                <tr style={{ color: COLORS.sub, textAlign: 'left' }}>
                                  {['No. Job', 'Item', 'Kategori', 'Judul', 'Qty', 'Harga', 'Total', 'Hutang'].map((h, i) => (
                                    <th key={h} style={{ padding: '8px 10px', fontSize: '10px', textTransform: 'uppercase', fontWeight: 600, textAlign: i >= 4 ? 'right' : 'left', borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {s.items.map((it, idx) => (
                                  <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.bgSoft}` }}>
                                    <td style={{ padding: '7px 10px', fontFamily: 'monospace', color: COLORS.primary }}>{it.jobNo}</td>
                                    <td style={{ padding: '7px 10px', color: COLORS.ink }}>{it.itemname}</td>
                                    <td style={{ padding: '7px 10px', color: COLORS.sub }}>{it.category}</td>
                                    <td style={{ padding: '7px 10px', color: COLORS.sub, maxWidth: '240px' }}>{it.judul}</td>
                                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{fmtNum(it.qty)}</td>
                                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{rupiah(it.sellingprice)}</td>
                                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{rupiah(it.total)}</td>
                                    <td style={{ padding: '7px 10px', textAlign: 'right', color: it.hutang > 0 ? '#dc2626' : COLORS.sub }}>{rupiah(it.hutang)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalStruk && <PelunasanModal struk={modalStruk} onClose={() => setModalStruk(null)} />}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default DigitalStrukReport;
